import { GetDashboardDailyTasksAPI } from "api/dashboard";
import { GetDashboardGuestSectionsAPI } from "api/dashboard";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { GetDashboardPreferenceInsightsAPI } from "api/dashboard";
import { GetDashboardReservationsAPI } from "api/dashboard";
import { GetDashboardOccupancyAPI } from "api/dashboard";

import { GetDashboardCountersAPI } from "api/dashboard";

export const getDashboardCounters = createAsyncThunk(
  "dashboard/getDashboardCounters",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetDashboardCountersAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch dashboard counters"
      );
    }
  }
);

export const getDashboardOccupancy = createAsyncThunk(
  "dashboard/getDashboardOccupancy",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetDashboardOccupancyAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch occupancy data"
      );
    }
  }
);

export const getDashboardReservations = createAsyncThunk(
  "dashboard/getDashboardReservations",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetDashboardReservationsAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch reservation chart"
      );
    }
  }
);

export const getDashboardPreferenceInsights = createAsyncThunk(
  "dashboard/getDashboardPreferenceInsights",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetDashboardPreferenceInsightsAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch preference insights"
      );
    }
  }
);

export const getDashboardGuestSections = createAsyncThunk(
  "dashboard/getDashboardGuestSections",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetDashboardGuestSectionsAPI(payload);
      if (response?.data?.success === false) {
        return rejectWithValue(response.data.message || "Failed to fetch guest sections");
      }
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message || "Failed to fetch guest sections");
    }
  }
);

export const getDashboardDailyTasks = createAsyncThunk(
  "dashboard/getDashboardDailyTasks",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetDashboardDailyTasksAPI(payload);
      if (response?.data?.success === false) {
        return rejectWithValue(response.data.message || "Failed to fetch daily tasks");
      }
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message || "Failed to fetch daily tasks");
    }
  }
);

const initialState = {
  dailyTasks: null,
  dailyTasksLoading: false,
  dailyTasksError: null,
  dailyTasksSuccess: false,
  dailyTasksRequestId: null,
  dailyTasksHotelId: null,

  guestSections: null,
  guestSectionsLoading: false,
  guestSectionsError: null,
  guestSectionsSuccess: false,
  guestSectionsRequestId: null,
  guestSectionsHotelId: null,

  // COUNTERS
  counters: null,
  countersLoading: false,
  countersError: null,
  countersSuccess: false,

  // OCCUPANCY
  occupancy: null,
  occupancyLoading: false,
  occupancyError: null,
  occupancySuccess: false,

  // RESERVATIONS
  reservations: null,
  reservationsLoading: false,
  reservationsError: null,
  reservationsSuccess: false,

  // PREFERENCE INSIGHTS
  preferenceInsights: null,
  preferenceInsightsLoading: false,
  preferenceInsightsError: null,
  preferenceInsightsSuccess: false,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,

  reducers: {
    resetDashboardDailyTasksState: (state) => {
      state.dailyTasks = null;
      state.dailyTasksLoading = false;
      state.dailyTasksError = null;
      state.dailyTasksSuccess = false;
      state.dailyTasksRequestId = null;
      state.dailyTasksHotelId = null;
    },
    resetDashboardGuestSectionsState: (state) => {
      state.guestSections = null;
      state.guestSectionsLoading = false;
      state.guestSectionsError = null;
      state.guestSectionsSuccess = false;
      state.guestSectionsRequestId = null;
      state.guestSectionsHotelId = null;
    },
    resetDashboardCountersState: (state) => {
      state.countersLoading = false;
      state.countersError = null;
      state.countersSuccess = false;
    },
    resetDashboardOccupancyState: (state) => {
      state.occupancyLoading = false;
      state.occupancyError = null;
      state.occupancySuccess = false;
    },

    resetDashboardReservationsState: (state) => {
      state.reservationsLoading = false;
      state.reservationsError = null;
      state.reservationsSuccess = false;
    },

    resetDashboardPreferenceInsightsState: (state) => {
      state.preferenceInsightsLoading = false;
      state.preferenceInsightsError = null;
      state.preferenceInsightsSuccess = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getDashboardDailyTasks.pending, (state, action) => {
        state.dailyTasksRequestId = action.meta.requestId;
        state.dailyTasksHotelId = action.meta.arg.hotelId;
        state.dailyTasks = null;
        state.dailyTasksLoading = true;
        state.dailyTasksError = null;
        state.dailyTasksSuccess = false;
      })
      .addCase(getDashboardDailyTasks.fulfilled, (state, action) => {
        if (state.dailyTasksRequestId !== action.meta.requestId) return;
        state.dailyTasksLoading = false;
        state.dailyTasksSuccess = true;
        state.dailyTasks = action.payload || null;
        state.dailyTasksRequestId = null;
      })
      .addCase(getDashboardDailyTasks.rejected, (state, action) => {
        if (state.dailyTasksRequestId !== action.meta.requestId) return;
        state.dailyTasksLoading = false;
        state.dailyTasksError = action.payload || action.error.message;
        state.dailyTasksSuccess = false;
        state.dailyTasksRequestId = null;
      })
      .addCase(getDashboardGuestSections.pending, (state, action) => {
        state.guestSectionsRequestId = action.meta.requestId;
        state.guestSectionsHotelId = action.meta.arg.hotelId;
        state.guestSections = null;
        state.guestSectionsLoading = true;
        state.guestSectionsError = null;
        state.guestSectionsSuccess = false;
      })
      .addCase(getDashboardGuestSections.fulfilled, (state, action) => {
        if (state.guestSectionsRequestId !== action.meta.requestId) return;
        state.guestSectionsLoading = false;
        state.guestSectionsSuccess = true;
        state.guestSections = action.payload || null;
        state.guestSectionsRequestId = null;
      })
      .addCase(getDashboardGuestSections.rejected, (state, action) => {
        if (state.guestSectionsRequestId !== action.meta.requestId) return;
        state.guestSectionsLoading = false;
        state.guestSectionsError = action.payload || action.error.message;
        state.guestSectionsSuccess = false;
        state.guestSectionsRequestId = null;
      })

      // ===========================
      // DASHBOARD COUNTERS
      // ===========================
      .addCase(getDashboardCounters.pending, (state, action) => {
        state.countersRequestId = action.meta.requestId;
        state.counters = null;
        state.countersLoading = true;
        state.countersError = null;
        state.countersSuccess = false;
      })

      .addCase(getDashboardCounters.fulfilled, (state, action) => {
        if (state.countersRequestId !== action.meta.requestId) return;
        state.countersLoading = false;
        state.countersSuccess = true;

        state.counters = action.payload || null;
      })

      .addCase(getDashboardCounters.rejected, (state, action) => {
        if (state.countersRequestId !== action.meta.requestId) return;
        state.countersLoading = false;
        state.countersError = action.payload;
      })
      // ===========================
      // OCCUPANCY
      // ===========================

      .addCase(getDashboardOccupancy.pending, (state, action) => {
        state.occupancyRequestId = action.meta.requestId;
        state.occupancy = null;
        state.occupancyLoading = true;
        state.occupancyError = null;
        state.occupancySuccess = false;
      })

      .addCase(getDashboardOccupancy.fulfilled, (state, action) => {
        if (state.occupancyRequestId !== action.meta.requestId) return;
        state.occupancyLoading = false;
        state.occupancySuccess = true;

        state.occupancy = action.payload || null;
      })

      .addCase(getDashboardOccupancy.rejected, (state, action) => {
        if (state.occupancyRequestId !== action.meta.requestId) return;
        state.occupancyLoading = false;
        state.occupancyError = action.payload;
      })
      // ===========================
      // RESERVATIONS
      // ===========================

      .addCase(getDashboardReservations.pending, (state, action) => {
        state.reservationsRequestId = action.meta.requestId;
        state.reservations = null;
        state.reservationsLoading = true;
        state.reservationsError = null;
        state.reservationsSuccess = false;
      })

      .addCase(getDashboardReservations.fulfilled, (state, action) => {
        if (state.reservationsRequestId !== action.meta.requestId) return;
        state.reservationsLoading = false;
        state.reservationsSuccess = true;

        state.reservations = action.payload || null;
      })

      .addCase(getDashboardReservations.rejected, (state, action) => {
        if (state.reservationsRequestId !== action.meta.requestId) return;
        state.reservationsLoading = false;
        state.reservationsError = action.payload;
      })

      // ===========================
      // PREFERENCE INSIGHTS
      // ===========================

      .addCase(getDashboardPreferenceInsights.pending, (state, action) => {
        state.preferenceInsightsRequestId = action.meta.requestId;
        state.preferenceInsights = null;
        state.preferenceInsightsLoading = true;
        state.preferenceInsightsError = null;
        state.preferenceInsightsSuccess = false;
      })

      .addCase(getDashboardPreferenceInsights.fulfilled, (state, action) => {
        if (state.preferenceInsightsRequestId !== action.meta.requestId) return;
        state.preferenceInsightsLoading = false;
        state.preferenceInsightsSuccess = true;

        state.preferenceInsights = action.payload || null;
      })

      .addCase(getDashboardPreferenceInsights.rejected, (state, action) => {
        if (state.preferenceInsightsRequestId !== action.meta.requestId) return;
        state.preferenceInsightsLoading = false;
        state.preferenceInsightsError = action.payload;
      });
  },
});

export const {
  resetDashboardDailyTasksState,
  resetDashboardGuestSectionsState,
  resetDashboardCountersState,
  resetDashboardOccupancyState,
  resetDashboardReservationsState,
  resetDashboardPreferenceInsightsState,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
